import axios from 'axios';
import { DEFAULT_COMPETITION_SLUG } from '../constants/competition';

/** Same base as AuthContext: REACT_APP_API_URL (e.g. http://localhost:5000). */
export const getApiBase = () =>
  (typeof process !== 'undefined' && process.env.REACT_APP_API_URL) || '';

/**
 * All paths are relative to axios.defaults.baseURL (set in AuthContext).
 * Competition routes: /api/competitions/:slug/...
 */
export function competitionRoot(slug = DEFAULT_COMPETITION_SLUG) {
  const s = encodeURIComponent(String(slug || DEFAULT_COMPETITION_SLUG).toLowerCase());
  return `/api/competitions/${s}`;
}

export async function fetchCompetitionsList() {
  const { data } = await axios.get('/api/competitions');
  return Array.isArray(data) ? data : [];
}

/** Admin: create competition (POST /api/competitions) */
export async function adminCreateCompetition(body) {
  const { data } = await axios.post('/api/competitions', body);
  return data;
}

/** Admin: update competition by slug or Mongo id */
export async function adminUpdateCompetition(slugOrId, body) {
  const { data } = await axios.put(competitionRoot(slugOrId), body);
  return data;
}

/** Admin: delete competition (no games) */
export async function adminDeleteCompetition(slugOrId) {
  const { data } = await axios.delete(competitionRoot(slugOrId));
  return data;
}

export async function fetchCompetitionOne(slugOrId) {
  const { data } = await axios.get(competitionRoot(slugOrId));
  return data;
}

export async function fetchCompetitionLeaderboard(slugOrId) {
  const { data } = await axios.get(`${competitionRoot(slugOrId)}/leaderboard`);
  return Array.isArray(data) ? data : [];
}

/** @param {'all'|'upcoming'|'finished'|undefined} filter */
export async function fetchCompetitionGames(slugOrId, filter) {
  const root = competitionRoot(slugOrId);
  let url = `${root}/games`;
  if (filter === 'upcoming') url = `${root}/games/upcoming`;
  else if (filter === 'finished') url = `${root}/games/finished`;
  const { data } = await axios.get(url);
  return Array.isArray(data) ? data : [];
}

export async function fetchCompetitionGame(slugOrId, gameId) {
  const { data } = await axios.get(`${competitionRoot(slugOrId)}/games/${gameId}`);
  return data;
}

export async function fetchMyBetsInCompetition(slugOrId) {
  const { data } = await axios.get(`${competitionRoot(slugOrId)}/bets/me`);
  return Array.isArray(data) ? data : [];
}

export async function postOrUpdateBet(slugOrId, { gameId, predictedResult }) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/bets`, {
    gameId,
    predictedResult
  });
  return data;
}

/** Admin: create game */
export async function adminCreateGame(slugOrId, body) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/games`, body);
  return data;
}

export async function adminUpdateGame(slugOrId, gameId, body) {
  const { data } = await axios.put(`${competitionRoot(slugOrId)}/games/${gameId}`, body);
  return data;
}

export async function adminSubmitGameResult(slugOrId, gameId, finalResult) {
  const { data } = await axios.put(`${competitionRoot(slugOrId)}/games/${gameId}/result`, {
    finalResult
  });
  return data;
}

export async function adminDeleteGame(slugOrId, gameId) {
  const { data } = await axios.delete(`${competitionRoot(slugOrId)}/games/${gameId}`);
  return data;
}

/** Admin: bulk delete games in a competition */
export async function adminBulkDeleteGames(slugOrId, gameIds) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/games/bulk-delete`, {
    gameIds
  });
  return data;
}

/** Admin: import games from CSV (server resolves team names against DB) */
export async function adminImportGamesCsv(slugOrId, csv) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/games/import-csv`, { csv });
  return data;
}

/** Teams (groups) for a competition — same slug as games/leaderboard. */
export function competitionGroupsPath(slugOrId) {
  return `${competitionRoot(slugOrId)}/groups`;
}

/** Admin: bulk delete teams (groups) in a competition */
export async function adminBulkDeleteGroups(slugOrId, groupIds) {
  const { data } = await axios.post(`${competitionGroupsPath(slugOrId)}/bulk-delete`, {
    groupIds
  });
  return data;
}

export function competitionQuestionsPath(slugOrId) {
  return `${competitionRoot(slugOrId)}/questions`;
}

export async function fetchCompetitionQuestions(slugOrId) {
  const { data } = await axios.get(competitionQuestionsPath(slugOrId));
  return Array.isArray(data) ? data : [];
}

export async function submitQuestionAnswer(slugOrId, questionId, body) {
  const { data } = await axios.post(
    `${competitionQuestionsPath(slugOrId)}/${questionId}/answer`,
    body
  );
  return data;
}

export async function adminSubmitQuestionAnswer(slugOrId, questionId, body) {
  const { data } = await axios.post(
    `${competitionQuestionsPath(slugOrId)}/${questionId}/answer/admin`,
    body
  );
  return data;
}

export async function adminCreateQuestion(slugOrId, body) {
  const { data } = await axios.post(competitionQuestionsPath(slugOrId), body);
  return data;
}

export async function adminUpdateQuestion(slugOrId, questionId, body) {
  const { data } = await axios.put(
    `${competitionQuestionsPath(slugOrId)}/${questionId}`,
    body
  );
  return data;
}

export async function adminDeleteQuestion(slugOrId, questionId) {
  const { data } = await axios.delete(
    `${competitionQuestionsPath(slugOrId)}/${questionId}`
  );
  return data;
}

export async function adminResolveQuestion(slugOrId, questionId, body) {
  const { data } = await axios.put(
    `${competitionQuestionsPath(slugOrId)}/${questionId}/resolve`,
    body
  );
  return data;
}

/** Admin: TheSportsDB league search */
export async function adminSearchSportsDbLeagues(search) {
  const { data } = await axios.get('/api/admin/sportsdb/leagues', {
    params: { search }
  });
  return data;
}

export async function adminGetSportsDbSeasons(leagueId) {
  const { data } = await axios.get(`/api/admin/sportsdb/leagues/${leagueId}/seasons`);
  return data;
}

/** @deprecated Use adminSearchSportsDbLeagues */
export async function adminSearchApiFootballLeagues(search) {
  return adminSearchSportsDbLeagues(search);
}

/** @deprecated Use adminGetSportsDbSeasons */
export async function adminGetApiFootballSeasons(leagueId) {
  return adminGetSportsDbSeasons(leagueId);
}

export async function adminSyncCompetition(slugOrId) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/sync`);
  return data;
}

export async function adminFetchImportReview(slugOrId) {
  const { data } = await axios.get(`${competitionRoot(slugOrId)}/import-review`);
  return data;
}

export async function adminUpdateImportReviewTeam(slugOrId, teamId, body) {
  const { data } = await axios.put(
    `${competitionRoot(slugOrId)}/import-review/teams/${teamId}`,
    body
  );
  return data;
}

export async function adminBulkUpdateImportReviewTeams(slugOrId, teams) {
  const { data } = await axios.put(`${competitionRoot(slugOrId)}/import-review/teams`, {
    teams
  });
  return data;
}

export async function adminApproveImportReview(slugOrId) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/import-review/approve`);
  return data;
}

/** Admin: re-score all finished games in this competition */
export async function adminRecalculateResults(slugOrId) {
  const { data } = await axios.post(`${competitionRoot(slugOrId)}/recalculate-results`);
  return data;
}

export async function adminFetchPendingResults(slugOrId) {
  const { data } = await axios.get(`${competitionRoot(slugOrId)}/pending-results`);
  return Array.isArray(data?.games) ? data.games : [];
}

export async function adminConfirmPendingResult(slugOrId, gameId) {
  const { data } = await axios.post(
    `${competitionRoot(slugOrId)}/games/${gameId}/confirm-result`
  );
  return data;
}

export async function adminDismissPendingResult(slugOrId, gameId) {
  const { data } = await axios.post(
    `${competitionRoot(slugOrId)}/games/${gameId}/dismiss-pending-result`
  );
  return data;
}
